import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import { ACCESS_TYPES } from '../../store/investor/constants';
import { MobileTabConfig } from './types';
import { MOBILE_TAB_CONFIG } from './MobileBottomTabs.constants';
import styles from './MobileBottomTabs.module.scss';

interface MobileBottomTabsProps {
    // Rendered as a flex sibling of the tab pill so the gap between them stays exact.
    trailingAction?: ReactNode;
}

const MobileBottomTabs = ({ trailingAction }: MobileBottomTabsProps = {}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const permissions = useAppSelector((state) => state.investor.permissions);

    const visibleTabs = MOBILE_TAB_CONFIG.filter((tab) => {
        if (!tab.permissionKey) return true;
        const perm = permissions[tab.permissionKey];
        return perm !== ACCESS_TYPES.LOCKED && perm !== ACCESS_TYPES.HIDDEN;
    });

    // No fallback to visibleTabs[0] — a page behind this bar that matches none of
    // the tabs must show nothing selected, not incorrectly highlight the first one.
    const activeTab = visibleTabs.find((tab) => location.pathname.includes(tab.route))?.key;

    const handleTabChange = (tab: MobileTabConfig) => {
        navigate(`../${tab.route}`, { relative: 'path' });
    };

    if (visibleTabs.length === 0) return null;

    return createPortal(
        <div className={clsx(styles.BottomTabsRow, { [styles.WithTrailingAction]: !!trailingAction })}>
            <div className={styles.BottomTabs}>
                {visibleTabs.map((tab) => {
                    const isActive = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            className={clsx(styles.Tab, {
                                [styles.Active]: isActive,
                            })}
                            onClick={() => handleTabChange(tab)}
                        >
                            <img
                                className={styles.Icon}
                                src={isActive ? tab.activeIcon : tab.icon}
                                alt={tab.label}
                                height={20}
                                width={20}
                            />
                            <span className={styles.Label}>{tab.label}</span>
                        </button>
                    );
                })}
            </div>
            {trailingAction}
        </div>,
        document.body,
    );
};

export default MobileBottomTabs;
